/**
 * src/pages/OriginAndBeyond/sections/HubHero.tsx
 * Hero Section for the Origin & Beyond hub page:
 * - Small eyebrow: "Origin & Beyond: The Greatest Questions of Existence"
 * - H1 exactly: "WHERE DID WE COME FROM. WHERE DO WE GO?"
 *   (Rendered uppercase, two sentences visually distinct, glowing, wraps cleanly at 320px)
 * - Subline
 * - Two buttons: "Begin the journey" (smooth-scroll to #journey-section) and "Read the evidence key" (smooth-scroll to #evidence-key-section)
 * - Signature visual: CosmicSpiralCanvas behind content
 */

import React from "react";
import { ArrowDown, Compass, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { CosmicSpiralCanvas } from "./CosmicSpiralCanvas";
import styles from "../OriginAndBeyond.module.css";
import "@/components/cosmic/cosmic.css";
export function HubHero() {
  return <section className={styles.hubHeroContainer} aria-labelledby="hub-hero-title">
      {/* Background signature canvas */}
      <div className={styles.canvasWrapper} aria-hidden="true">
        <CosmicSpiralCanvas />
      </div>

      {/* Main hero typography & CTAs */}
      <div className={styles.heroContent}>
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-md mb-2">
          <Sparkles className="size-3.5 text-primary" aria-hidden="true" />
          <span className="font-serif text-[11px] sm:text-xs font-semibold tracking-widest text-primary uppercase">
            Origin & Beyond: The Greatest Questions of Existence
          </span>
        </div>

        {/* Big exact H1 */}
        <h1 id="hub-hero-title" className={styles.heroTitle}>
          <span className={styles.heroTitleSentence1}>
            WHERE DID WE COME FROM.
          </span>
          <span className={styles.heroTitleSentence2}>
            WHERE DO WE GO?
          </span>
        </h1>

        {/* Subline */}
        <p className="max-w-2xl text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed font-sans px-2 mb-8">
          Step into a four-stage inquiry exploring the boundary between empirical cosmology,
          neural models of subjective mind, thermodynamics of mortality, and the ultimate
          fates of deep time.
        </p>

        {/* Dual CTA buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full max-w-md px-2">
          <Link to="#journey-section" className="ql-btn-neon w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[48px] px-6 py-3 font-serif text-xs sm:text-sm tracking-wider uppercase font-bold text-center no-underline">
            <span>Begin the journey</span>
            <ArrowDown className="size-4 shrink-0" aria-hidden="true" />
          </Link>

          <Link to="#evidence-key-section" className="ql-btn-glass w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[48px] px-6 py-3 font-serif text-xs sm:text-sm tracking-wider uppercase text-center no-underline">
            <Compass className="size-4 shrink-0 text-primary" aria-hidden="true" />
            <span>Read the evidence key</span>
          </Link>
        </div>
      </div>
    </section>;
}